import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import BookLink from "./BookLink";
import { Menu, X, Calendar } from "lucide-react";
import Logo from "./Logo";
import { nav, business } from "../data/content";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      {business.announcement.enabled && (
        <div
          className="w-full bg-petal text-white text-xs sm:text-sm font-body tracking-wide py-2.5 text-center px-4"
          data-testid="announcement-bar"
        >
          <span className="opacity-95">{business.announcement.text}</span>
        </div>
      )}

      <header
        className={`sticky top-0 z-40 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
          scrolled
            ? "bg-cream/85 backdrop-blur-md shadow-[0_6px_20px_-16px_rgba(190,60,120,0.35)]"
            : "bg-cream/60 backdrop-blur-sm"
        } border-b border-pale-rose/40 overflow-visible`}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 py-2.5 overflow-visible">
          <Link
            to="/"
            aria-label="Soft Petal Electrolysis, Home"
            data-testid="header-logo-link"
            className="relative flex items-center -my-2"
          >
            <Logo className="h-14 sm:h-16 lg:h-20 w-auto" priority />
          </Link>

          <nav aria-label="Primary" className="hidden lg:flex items-center gap-8">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-body text-[0.82rem] tracking-[0.22em] uppercase transition-colors duration-200 ${
                    isActive
                      ? "text-petal"
                      : "text-charcoal hover:text-petal"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <BookLink  className="btn-petal hidden sm:inline-flex" data-testid="header-book-btn">
              <Calendar size={14} strokeWidth={2} />
              Book Now
            </BookLink>
            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-pale-rose/60 text-charcoal hover:text-petal hover:border-petal/50 transition-colors"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-nav"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        <div
          id="mobile-nav"
          className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
            open ? "max-h-[560px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <nav aria-label="Mobile" className="px-5 pb-6 pt-2 flex flex-col gap-1 bg-cream/95 backdrop-blur-md">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-body py-3 px-3 rounded-xl text-[0.9rem] tracking-[0.18em] uppercase ${
                    isActive
                      ? "text-petal bg-blush/60"
                      : "text-charcoal hover:bg-blush/40"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <BookLink  className="btn-petal mt-3 justify-center" data-testid="mobile-book-btn">
              <Calendar size={14} />
              Book Now
            </BookLink>
          </nav>
        </div>
      </header>
    </>
  );
}
